import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import iconXpStudent from '../../../assets/student/Icon-xp-student.png';
import { useTheme } from '../../theme';

export interface StudentXpBannerProps extends HTMLAttributes<HTMLElement> {
  label: ReactNode;
  value: ReactNode;
  /** Se omitido, usa a tradução `student_dashboard.xp_banner.unit`. */
  unitLabel?: ReactNode;
  /** Substitui o ícone padrão (troféu); se omitido, usa `Icon-xp-student.png` */
  iconSlot?: ReactNode;
}

export function StudentXpBanner({
  label,
  value,
  unitLabel,
  iconSlot,
  style,
  ...rest
}: StudentXpBannerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const xp = theme.palette.studentDashboard.xpBanner;
  const resolvedUnit = unitLabel !== undefined ? unitLabel : t('student_dashboard.xp_banner.unit');

  return (
    <section
      style={{
        boxSizing: 'border-box',
        width: xp.width,
        height: xp.height,
        borderRadius: theme.shape.borderRadius.lg,
        background: xp.gradient,
        border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
        color: theme.palette.text.white,
        padding: theme.spacing.lg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...style,
      }}
      {...rest}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.xs,
        }}
      >
        <span
          style={{
            fontSize: theme.typography.fontSize.sm,
            color: xp.labelMuted,
          }}
        >
          {label}
        </span>
        <p
          style={{
            margin: 0,
            display: 'flex',
            alignItems: 'baseline',
            gap: theme.spacing.xs,
          }}
        >
          <strong
            style={{
              fontSize: theme.typography.fontSize['5xl'],
              fontWeight: theme.typography.fontWeight.bold,
              lineHeight: xp.valueLineHeight,
              color: xp.valueColor,
            }}
          >
            {value}
          </strong>
          <span
            style={{
              fontSize: theme.typography.fontSize.sm,
              color: xp.unitMuted,
            }}
          >
            {resolvedUnit}
          </span>
        </p>
      </div>
      <div
        style={{
          width: xp.trophyBoxSize,
          height: xp.trophyBoxSize,
          minWidth: xp.trophyBoxSize,
          minHeight: xp.trophyBoxSize,
          borderRadius: theme.shape.borderRadius.full,
          display: 'grid',
          placeItems: 'center',
          backgroundColor: xp.trophyBoxBackground,
          flexShrink: 0,
        }}
        aria-hidden
      >
        {iconSlot ?? (
          <img
            src={iconXpStudent}
            alt=""
            width={xp.trophyIconImageSize}
            height={xp.trophyIconImageSize}
            style={{ display: 'block', objectFit: 'contain' }}
          />
        )}
      </div>
    </section>
  );
}
